/**
 * Apuntar el cobro de una factura: entera o a plazos.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 24-sep-2026: "al pasar una factura a pagada no me deja decir que es
 * por pago parcial, es decir que la han pagado entera o que han pagado una
 * parte y queda otra por pagar, y esto nos ocurre con frecuencia".
 *
 * Antes era un `¿Estás seguro?` y la factura quedaba pagada entera. Cobrar la
 * mitad no se podía contar: marcarla pagada escondía lo que falta, y dejarla
 * pendiente escondía lo cobrado.
 *
 * QUÉ SE PREGUNTA Y QUÉ NO
 * ------------------------
 * La fecha siempre: es la del dinero, no la de cuando se apunta, y es la que
 * cuadra el extracto del banco con los libros.
 *
 * Entera o a plazos, solo cuando tiene sentido. En las repetitivas y en las
 * que van por SEPA no se pregunta: el banco gira el recibo entero o no lo
 * gira. Ver `admitePagoParcial`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { mensajeDeError } from '@/lib/errores';
import {
  admitePagoParcial, cuerpoDeCobro, esAbono, motivoParaNoCobrar, resumenDeCobro,
} from '@/lib/cobros';

/**
 * Un importe como se lee aquí: "60,38 €", no "60.38 €".
 *
 * El campo del importe se rellena con coma —es como se teclea— y las
 * etiquetas lo decían con punto. Dos formatos en el mismo diálogo hacen
 * dudar de cuál espera el campo, que es justo lo que ya costó un rato
 * (Angel, 7-oct-2026).
 */
const euros = (n) => `${(Number(n) || 0).toLocaleString('es-ES', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} €`;

const hoyEnInput = () => {
  const d = new Date();
  // Sin toISOString: pasa a UTC y en España adelanta el día por la tarde.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function DialogoCobro({ invoice, open, onOpenChange, onHecho }) {
  const resumen = useMemo(() => resumenDeCobro(invoice), [invoice]);
  // En un abono el dinero SALE. Todo el diálogo cambia de palabras: decir
  // "ha pagado" sobre una devolución hace dudar de si la pantalla ha
  // entendido qué documento tiene delante (Angel, 7-oct-2026).
  const abono = useMemo(() => esAbono(invoice), [invoice]);
  const voz = abono
    ? { titulo: 'Apuntar devolución', accion: 'Apuntar devolución',
        pregunta: '¿Cuánto le has devuelto?',
        comoPaga: '¿Cómo se lo has devuelto?',
        dia: '¿Qué día salió el dinero?',
        yaHecho: 'Ya devuelto', queda: 'Queda por devolver',
        aviso: 'El abono quedará devuelto del todo. Apúntalo solo si el dinero ha salido.',
        haciendo: 'Apuntando...' }
    : { titulo: 'Apuntar cobro', accion: 'Apuntar cobro',
        pregunta: '¿Cuánto ha pagado?',
        comoPaga: '¿Cómo ha pagado?',
        dia: '¿Qué día entró el dinero?',
        yaHecho: 'Ya cobrado', queda: 'Queda por cobrar',
        aviso: 'La factura quedará pagada. Apúntalo solo si el dinero ha entrado.',
        haciendo: 'Apuntando...' };
  const permiteParcial = useMemo(() => admitePagoParcial(invoice), [invoice]);

  const [parcial, setParcial] = useState(false);
  const [importe, setImporte] = useState('');
  const [fecha, setFecha] = useState('');
  const [metodo, setMetodo] = useState('');
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setParcial(false);
    // Por defecto, lo que falta: es lo que se va a apuntar casi siempre.
    // Con coma, que es como se escribe y como se va a releer.
    setImporte(resumen.pendiente.toFixed(2).replace('.', ','));
    setFecha(hoyEnInput());
    setMetodo(invoice?.payment_method || '');
    setNota('');
  }, [open, invoice, resumen.pendiente]);

  const formulario = { parcial, importe, fecha, metodo, nota };
  const pega = motivoParaNoCobrar(formulario, invoice);

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await axiosInstance.post(
        `/invoices/${invoice.id}/cobro`, cuerpoDeCobro(formulario));
      toast.success(data.message);
      onOpenChange(false);
      onHecho?.(data);
    } catch (error) {
      toast.error(mensajeDeError(error));
    } finally {
      setGuardando(false);
    }
  };

  // Lo que quedaría después de apuntar esto.
  const quedaria = parcial
    ? Math.max(0, resumen.pendiente - (Number(importe) || 0))
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialogo-cobro">
        <DialogHeader>
          <DialogTitle>{voz.titulo}</DialogTitle>
          <DialogDescription>
            {invoice?.invoice_number} · {invoice?.customer_name} · {euros(invoice?.total)}
          </DialogDescription>
        </DialogHeader>

        {/* Lo ya cobrado, solo si hay algo. En una factura nueva sería ruido. */}
        {resumen.cobrado > 0 && (
          <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-600">{voz.yaHecho}</span>
              <span className="font-medium">{euros(resumen.cobrado)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">{voz.queda}</span>
              <span className="font-bold text-[#9C0F17]">{euros(resumen.pendiente)}</span>
            </div>
          </div>
        )}

        {permiteParcial ? (
          <div className="space-y-2">
            <Label>{voz.comoPaga}</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={parcial ? 'outline' : 'default'}
                onClick={() => setParcial(false)}
                className={parcial ? '' : 'bg-[#9C0F17] hover:bg-[#7A0C12] text-white'}
                data-testid="cobro-entero"
              >
                Entera ({euros(resumen.pendiente)})
              </Button>
              <Button
                type="button"
                variant={parcial ? 'default' : 'outline'}
                onClick={() => setParcial(true)}
                className={parcial ? 'bg-[#9C0F17] hover:bg-[#7A0C12] text-white' : ''}
                data-testid="cobro-parcial"
              >
                Una parte
              </Button>
            </div>
          </div>
        ) : (
          /* Repetitivas y SEPA: el banco gira el recibo entero o no lo gira.
             Se dice, para que no parezca que falta una opción. */
          <p className="text-xs text-slate-500">
            Se cobra entera: las facturas que van por recibo domiciliado las
            gira el banco completas.
          </p>
        )}

        {parcial && (
          <div className="space-y-1.5">
            <Label htmlFor="importe-cobro">{voz.pregunta} *</Label>
            {/* `type="text"` y no `type="number"`: con number, teclear
                "54,33" deja `e.target.value` VACÍO -- el navegador
                considera la coma inválida -- aunque en pantalla se siga
                viendo lo tecleado. El importe se quedaba en 0 y el botón
                no se habilitaba nunca. `inputMode="decimal"` mantiene el
                teclado numérico en el móvil. */}
            <Input
              id="importe-cobro" type="text" inputMode="decimal"
              value={importe}
              onChange={(e) => setImporte(e.target.value)}
              placeholder="0,00"
              data-testid="importe-cobro"
            />
            <p className="text-xs text-slate-500">
              Después quedarían <strong>{euros(quedaria)}</strong> por cobrar.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="fecha-cobro">{voz.dia} *</Label>
          <Input
            id="fecha-cobro" type="date" value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            data-testid="fecha-cobro"
          />
          <p className="text-xs text-slate-500">
            La fecha real del cobro, no la de hoy: es la que cuadra con el extracto.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>¿Cómo ha entrado?</Label>
          <Select value={metodo || ''} onValueChange={setMetodo}>
            <SelectTrigger data-testid="metodo-cobro">
              <SelectValue placeholder="La forma de pago de la factura" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="transferencia">💸 Transferencia</SelectItem>
              <SelectItem value="sepa">🏦 Recibo domiciliado</SelectItem>
              <SelectItem value="efectivo">💵 Efectivo</SelectItem>
              <SelectItem value="tarjeta">💳 Tarjeta</SelectItem>
              <SelectItem value="link">🔗 Link de pago</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="nota-cobro">Nota (opcional)</Label>
          <Input
            id="nota-cobro" type="text" value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Ej: a cuenta, paga el resto en octubre"
            data-testid="nota-cobro"
          />
        </div>

        {!parcial && (
          <p className="text-xs text-amber-700 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {voz.aviso}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={Boolean(pega) || guardando}
                  className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                  title={pega || undefined}
                  data-testid="confirmar-cobro">
            {guardando ? 'Apuntando...' : voz.accion}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DialogoCobro;
